import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorNotification, showSuccessNotification } from '@/components/common/utils/notify';

export interface SavingsProductDTO {
    glNo: string;
    savGlno?: string;
    lguarPer?: number;
    range1fr?: number;
    range1to?: number;
    range1pe?: number;
    range2fr?: number;
    range2to?: number;
    range2pe?: number;
    range3fr?: number;
    range3to?: number;
    range3pe?: number;
}

const BASE = '/api/admin/products/savings';

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || res.statusText);
    }
    return res.json();
}

export const useSavingsProducts = () =>
    useQuery<SavingsProductDTO[]>({
        queryKey: ['savings-products'],
        queryFn: () => apiFetch(BASE),
    });

export const useSavingsProduct = (glNo: string | undefined) =>
    useQuery<SavingsProductDTO>({
        queryKey: ['savings-products', glNo],
        queryFn: () => apiFetch(`${BASE}/${glNo}`),
        enabled: !!glNo,
    });

export const useUpdateSavingsProduct = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: SavingsProductDTO) =>
            apiFetch<SavingsProductDTO>(`${BASE}/${dto.glNo}`, {
                method: 'PUT',
                body: JSON.stringify(dto),
            }),
        onSuccess: (_, vars) => {
            qc.invalidateQueries({ queryKey: ['savings-products'] });
            qc.invalidateQueries({ queryKey: ['savings-products', vars.glNo] });
            showSuccessNotification('Savings product updated successfully');
        },
        onError: (e: Error) => showErrorNotification(e.message),
    });
};
